import React, { useState, useEffect, useCallback } from "react";
import { Card, Col, Nav, Row, Table, Button, Modal, Form, Alert } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import SectionHeader from "./HeaderSection";
import { WhiteBoxWrapper } from "./Login.style";
import { FaArrowLeft, FaTrash } from "react-icons/fa";
import { Device } from "./DeviceCard";
import { User } from "./UserCard";
import { getCountBadge } from "../utils/getCountBadge";
import { getStatusBadge } from "../utils/getStatusBadge";
import { fetchAPI } from "../utils/fetchAPI";
import { getReminderVariant } from "../utils/getReminderVariant";

// Enum dla statusów urządzenia
export enum DeviceStatus {
  Active = 1,
  InRepair = 2,
  Broken = 3,
  Replacement = 4,
}

// Typy danych
interface UsageEntry {
  user: User | null;
  count: number;
}

interface HistoryLog {
  Info: string;
  datelog: string;
  user: User | null;
}

interface Reminder {
  id: number;
  id_user: number;
  id_device: number;
  reminder_date: string;
  description: string;
}

const DeviceDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [device, setDevice] = useState<Device | null>(null);
  const [usage, setUsage] = useState<UsageEntry[]>([]);
  const [history, setHistory] = useState<HistoryLog[]>([]);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [categoryList, setCategoryList] = useState<string[]>([]);
  const [typeList, setTypeList] = useState<string[]>([]);
  const [newStatus, setNewStatus] = useState<DeviceStatus | null>(null);
  const [newName, setNewName] = useState("");
  const [note, setNote] = useState("");
  const [category, setCategory] = useState("");
  const [type, setType] = useState("");
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newCount, setNewCount] = useState<DeviceStatus | null>(null);
  const [showCountModal, setShowCountModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationDate, setNotificationDate] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState<
    Array<{
      id: number;
      file_name: string;
      file_path: string;
      file_type: string;
      file_size: number;
      upload_date: string;
    }>
  >([]);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState("");
  const [fileName, setFileName] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const fetchDeviceDetails = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const data = await fetchAPI("GET", `device.php?id=${id}`);

      if (data.success) {
        setDevice(data.device);
        setCategory(data.device.category || "");
        setType(data.device.type || "");
        setNewStatus(data.device.status as DeviceStatus | null);
        setUsage(data.usage || []);
        setHistory(data.history || []);
        setReminders(data.reminders || []);
        setCategoryList(data.categoryList || []);
        setTypeList(data.typeList || []);
      } else {
        throw new Error(data.message || "Nie udało się pobrać danych urządzenia.");
      }
    } catch (error: any) {
      setErrorMessage(error.message || "Wystąpił błąd podczas pobierania danych.");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const handleChangeStatus = async () => {
    if (!newStatus) return;
    try {
      const data = await fetchAPI("POST", "setDeviceStatus.php", {
        deviceId: id,
        status: newStatus,
        note: note,
        category: category,
        type: type,
        name: newName,
      });

      if (data.success) {
        setShowStatusModal(false);
        fetchDeviceDetails();
      } else {
        throw new Error("Błąd podczas zmiany statusu");
      }
    } catch (error) {
      setErrorMessage((error as Error).message);
    }
  };

  const handleAddNotification = async () => {
    if (!notificationDate || !description || !device) {
      alert("Uzupełnij wszystkie pola");
      return;
    }

    try {
      const response = await fetchAPI("POST", "addNotification.php", {
        deviceId: device.id,
        date: notificationDate,
        description: description,
      });

      if (response.success) {
        setShowNotificationModal(false);
        // np. odświeżenie listy powiadomień:
        fetchDeviceDetails?.();
      } else {
        throw new Error("Nie udało się dodać powiadomienia");
      }
    } catch (error) {
      setErrorMessage((error as Error).message);
    }
  };

  const handleChangeCount = async () => {
    if (!newCount) return;
    try {
      const data = await fetchAPI("POST", "setDeviceCount.php", { deviceId: id, count: newCount });

      if (data.success) {
        setShowCountModal(false);
        fetchDeviceDetails();
      } else {
        throw new Error("Błąd podczas zmiany stanu magazynowego");
      }
    } catch (error) {
      setErrorMessage((error as Error).message);
    }
  };

  const handleAvatarUpdate = async () => {
    if (!avatarFile) return;
    const formData = new FormData();
    formData.append("deviceId", id as string);
    formData.append("avatar", avatarFile);
    try {
      const response = await fetchAPI("POST", "setDeviceAvatar.php", formData);
      if (response.success) {
        setShowAvatarModal(false);
        fetchDeviceDetails();
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      setErrorMessage((error as Error).message);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 10 * 1024 * 1024) {
        // 10MB limit
        setUploadError("Plik jest zbyt duży. Maksymalny rozmiar to 10MB.");
        return;
      }
      setSelectedFile(file);
      setFileName(file.name);
      setUploadError("");
    }
  };

  const uploadFile = async () => {
    if (!selectedFile || !id || !fileName) return;

    const formData = new FormData();
    formData.append("device_id", id);
    formData.append("file", selectedFile);
    formData.append("file_name", fileName);

    try {
      const data = await fetchAPI("POST", "uploadFile.php", formData);
      if (data.success) {
        setShowFileUploadModal(false);
        setSelectedFile(null);
        setFileName("");
        fetchFiles();
      } else {
        setUploadError(data.message || "Wystąpił błąd podczas przesyłania pliku");
      }
    } catch (error) {
      setUploadError("Wystąpił błąd podczas przesyłania pliku");
    }
  };

  const fetchFiles = useCallback(async () => {
    if (!id) return;
    try {
      const data = await fetchAPI("GET", `getFiles.php?device_id=${id}`);
      if (data.success) {
        setFiles(data.files);
      }
    } catch (error) {
      console.error("Error fetching files:", error);
    }
  }, [id]);

  const handleDeleteDevice = async () => {
    try {
      setIsLoading(true);
      const response = await fetchAPI("DELETE", "deleteDevice.php", { id: id });

      if (response.success) {
        alert("Urządzenie zostało usunięte.");
        navigate("/devices");
      } else {
        throw new Error(response.message || "Nie udało się usunąć urządzenia.");
      }
    } catch (error: any) {
      setErrorMessage(error.message || "Wystąpił błąd podczas usuwania urządzenia.");
    } finally {
      setIsLoading(false);
      setShowDeleteModal(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchDeviceDetails();
      fetchFiles();
    }
  }, [fetchDeviceDetails, id]);

  useEffect(() => {
    if (device) {
      setNewName(device.name);
    }
  }, [device]);

  return (
    <div>
      <WhiteBoxWrapper>
        <Row className="align-items-center">
          <Col xs={1}>
            <h2>
              <Nav.Link as={Link} to="/devices">
                <FaArrowLeft size={24} />
              </Nav.Link>
            </h2>
          </Col>
          <Col xs={11}>
            <h2 className="text-center">
              <b>
                {device?.name} #{id}
              </b>
            </h2>
          </Col>
        </Row>
      </WhiteBoxWrapper>

      <Card.Img
        src={device?.avatar ? device?.avatar : "../assets/img/device.png"}
        style={{
          width: "100%",
          height: "auto",
          objectFit: "cover",
          borderRadius: "8px",
          marginBottom: "10px",
        }}
      />
      <Button variant="secondary" onClick={() => setShowAvatarModal(true)}>
        Zmień Avatar
      </Button>

      <hr />
      <Modal show={showAvatarModal} onHide={() => setShowAvatarModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Zmień Avatar Urządzenia</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Wybierz plik</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAvatarFile(e.target.files?.[0] || null)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAvatarModal(false)}>
            Anuluj
          </Button>
          <Button variant="primary" onClick={handleAvatarUpdate} disabled={!avatarFile}>
            Zapisz zmiany
          </Button>
        </Modal.Footer>
      </Modal>

      {device && (
        <>
          <Card className="p-2 bg-brand">
            <SectionHeader>Informacje o wyposażeniu</SectionHeader>
            <p>
              <b>Opis:</b> {device.description}
            </p>
            <p>
              <b>Lokalizacja:</b> {device.category}
            </p>
            <p>
              <b>Rodzaj:</b> {device.type}
            </p>
            <p>
              <b>Stacjonarne:</b> {device.returnable ? "Tak" : "Nie"}
            </p>
            <p>
              {getStatusBadge(device.status)}
              <hr />
              <Button variant="secondary" onClick={() => setShowStatusModal(true)}>
                Edycja wyposażenia
              </Button>
              <hr />
              <Button variant="secondary" onClick={() => setShowNotificationModal(true)}>
                Dodaj powiadomienie
              </Button>
            </p>
            {device.returnable === 0 && (
              <p>
                {getCountBadge(device.count)}
                <hr />
                <Button variant="secondary" onClick={() => setShowCountModal(true)}>
                  Aktualizuj stan magazynowy
                </Button>
              </p>
            )}
          </Card>

          {reminders.length > 0 && (
            <Card className="p-2 bg-brand mt-4">
              <SectionHeader>Aktywne powiadomienia</SectionHeader>
              {reminders.map((reminder, index) => {
                const variant = getReminderVariant(reminder.reminder_date);
                const today = new Date();
                const reminderDate = new Date(reminder.reminder_date);
                const diffTime = reminderDate.getTime() - today.getTime();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                return (
                  <Alert key={index} variant={variant} className="mt-2">
                    <Alert.Heading>Powiadomienie</Alert.Heading>
                    <p>
                      <b>Data:</b> {reminderDate.toLocaleDateString()}
                    </p>
                    <p>
                      <b>Pozostało dni:</b> {diffDays}
                    </p>
                    <p>
                      <b>Opis:</b> {reminder.description}
                    </p>
                  </Alert>
                );
              })}
            </Card>
          )}

          {/* Modal potwierdzenia usunięcia */}
          <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>Potwierdzenie usunięcia</Modal.Title>
            </Modal.Header>
            <Modal.Body>Czy na pewno chcesz usunąć urządzenie {device.name}? Tej operacji nie można cofnąć.</Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                Anuluj
              </Button>
              <Button variant="danger" onClick={handleDeleteDevice}>
                Usuń
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}

      {device?.returnable === 0 && (
        <Card className="p-2 bg-brand mt-4">
          <SectionHeader>Podsumowanie wydań</SectionHeader>
          {usage.length > 0 ? (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Użytkownik</th>
                  <th>Liczba sztuk</th>
                </tr>
              </thead>
              <tbody>
                {usage.map((entry, index) => (
                  <tr key={index}>
                    <td>
                      {entry.user ? (
                        <span onClick={() => navigate(`/user/${entry.user?.id}`)}>
                          <b>
                            {entry.user.name} {entry.user.surname}
                          </b>
                        </span>
                      ) : (
                        "Nieznany użytkownik"
                      )}
                    </td>
                    <td>{entry.count}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p>Brak danych o pobraniu sprzętu.</p>
          )}
        </Card>
      )}

      <Card className="p-2 bg-brand mt-4">
        <SectionHeader>Historia Logów</SectionHeader>
        {history.length > 0 ? (
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Akcja</th>
                <th>Użytkownik</th>
                <th>Data</th>
              </tr>
            </thead>
            <tbody>
              {history.map((log, index) => (
                <tr key={index}>
                  <td>{log.Info}</td>
                  <td>
                    {log.user ? (
                      <span onClick={() => navigate(`/user/${log.user?.id}`)}>
                        <b>
                          {log.user.name} {log.user.surname}
                        </b>
                      </span>
                    ) : (
                      "Nieznany użytkownik"
                    )}
                  </td>
                  <td>{new Date(log.datelog).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p>Brak historii do wyświetlenia.</p>
        )}
      </Card>

      <Card className="p-2 bg-brand mt-4">
        <SectionHeader>Załączniki</SectionHeader>
        <Button variant="secondary" onClick={() => setShowFileUploadModal(true)} className="mb-3">
          Dodaj załącznik
        </Button>

        {files.length > 0 ? (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Nazwa pliku</th>
                  <th>Typ</th>
                  <th>Rozmiar</th>
                  <th>Data dodania</th>
                  <th>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {files.map((file) => (
                  <tr key={file.id}>
                    <td>{file.file_name}</td>
                    <td>{file.file_type}</td>
                    <td>{(file.file_size / 1024).toFixed(2)} KB</td>
                    <td>{new Date(file.upload_date).toLocaleString()}</td>
                    <td>
                      <a
                        href={file.file_path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-primary"
                      >
                        Pobierz
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center">Brak załączników.</p>
        )}
      </Card>

      <Button variant="danger" className="w-100 mt-3" onClick={() => setShowDeleteModal(true)}>
        <FaTrash /> Usuń Urządzenie
      </Button>

      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edytuj Wyposażenie</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Label>Status</Form.Label>
          <Form.Select value={newStatus ?? ""} onChange={(e) => setNewStatus(Number(e.target.value) as DeviceStatus)}>
            <option value={newStatus ? newStatus : ""}>Wybierz status</option>
            <option value={DeviceStatus.Active}>Aktywne</option>
            <option value={DeviceStatus.InRepair}>W naprawie</option>
            <option value={DeviceStatus.Broken}>Uszkodzone</option>
            <option value={DeviceStatus.Replacement}>Zastępcze</option>
          </Form.Select>
          <Form.Group controlId="deviceName" className="mt-3">
            <Form.Label>Nazwa</Form.Label>
            <Form.Control
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Zmień nazwę"
            />
          </Form.Group>
          <Form.Group controlId="deviceCategory" className="mt-3">
            <Form.Label>Lokalizacja</Form.Label>
            <Form.Control
              as="input"
              list="suggestions"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="Zmień lokalizację"
            />
            <datalist id="suggestions">
              {categoryList.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </datalist>
          </Form.Group>
          <Form.Group controlId="deviceType" className="mt-3">
            <Form.Label>Rodzaj</Form.Label>
            <Form.Control
              as="input"
              list="type-suggestions"
              value={type}
              onChange={(e) => setType(e.target.value)}
              placeholder="Zmień rodzaj"
            />
            <datalist id="type-suggestions">
              {typeList.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </datalist>
          </Form.Group>
          <Form.Group controlId="deviceNote" className="mt-3">
            <Form.Label>Notatka</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Dodaj notatkę"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
            Anuluj
          </Button>
          <Button variant="primary" onClick={handleChangeStatus}>
            Zapisz zmiany
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCountModal} onHide={() => setShowCountModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Dodaj do stanu magazynowego</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Ilość do dodania</Form.Label>
            <Form.Control type="number" value={newCount ?? ""} onChange={(e) => setNewCount(Number(e.target.value))} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCountModal(false)}>
            Anuluj
          </Button>
          <Button variant="primary" onClick={handleChangeCount}>
            Zapisz zmiany
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showNotificationModal} onHide={() => setShowNotificationModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Dodaj powiadomienie</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3" controlId="notificationDate">
              <Form.Label>Data powiadomienia</Form.Label>
              <Form.Control
                type="date"
                value={notificationDate}
                onChange={(e) => setNotificationDate(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="notificationDescription">
              <Form.Label>Opis</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Wpisz treść powiadomienia"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNotificationModal(false)}>
            Anuluj
          </Button>
          <Button variant="primary" onClick={handleAddNotification}>
            Dodaj powiadomienie
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showFileUploadModal} onHide={() => setShowFileUploadModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Dodaj załącznik</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="fileUpload">
            <Form.Label>Wybierz plik (PDF, JPG, PNG)</Form.Label>
            <Form.Control type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileUpload} />
          </Form.Group>
          <Form.Group controlId="fileName" className="mt-3">
            <Form.Label>Nazwa załącznika</Form.Label>
            <Form.Control
              type="text"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              placeholder="Wprowadź nazwę załącznika"
            />
          </Form.Group>
          {uploadError && <div className="text-danger mt-2">{uploadError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFileUploadModal(false)}>
            Anuluj
          </Button>
          <Button variant="primary" onClick={uploadFile} disabled={!selectedFile || !fileName}>
            Prześlij
          </Button>
        </Modal.Footer>
      </Modal>

      {isLoading && <p>Ładowanie danych...</p>}
      {errorMessage && <p className="error">{errorMessage}</p>}

      {!isLoading && !errorMessage && !device && <p>Brak danych do wyświetlenia.</p>}
    </div>
  );
};

export default DeviceDetails;
